import { useEffect, useState } from "react";

/**
 * Dev/QA overlay for verifying CTA tracking fires correctly.
 * Activate with ?ctaqa=1 (or localStorage.setItem('ctaqa','1')).
 * Listens to the `cta:tracked` CustomEvent dispatched from src/lib/tracking.ts.
 */
type Entry = { id: number; name: string; props: Record<string, unknown>; ts: number };

export const CTAEventQA = () => {
  const [enabled, setEnabled] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const on = params.get("ctaqa") === "1" || localStorage.getItem("ctaqa") === "1";
    if (params.get("ctaqa") === "1") localStorage.setItem("ctaqa", "1");
    if (params.get("ctaqa") === "0") localStorage.removeItem("ctaqa");
    setEnabled(on);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let i = 0;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ name: string; props: Record<string, unknown> }>).detail;
      setEntries((prev) => [{ id: ++i, name: detail.name, props: detail.props, ts: Date.now() }, ...prev].slice(0, 25));
    };
    window.addEventListener("cta:tracked", handler);
    return () => window.removeEventListener("cta:tracked", handler);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-2 right-2 z-[60] max-w-[360px] font-mono text-[11px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left bg-gold text-navy-deep px-3 py-1.5 font-bold uppercase tracking-[0.18em] shadow-cta"
      >
        CTA QA · {entries.length} {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="max-h-[55vh] overflow-auto bg-navy-deep/95 border border-gold/40 backdrop-blur-md">
          {entries.length === 0 && (
            <div className="p-3 text-silver">Waiting for CTA clicks…</div>
          )}
          {entries.map((e) => (
            <div key={e.id} className="border-b border-border/60 px-3 py-2">
              <div className="text-gold">{e.name}</div>
              <div className="text-silver/80 truncate">{JSON.stringify(e.props)}</div>
            </div>
          ))}
          {entries.length > 0 && (
            <button
              onClick={() => setEntries([])}
              className="w-full bg-navy-elevated text-silver hover:text-white py-1.5 uppercase tracking-[0.18em]"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};
